import React, { useEffect, useMemo, useState } from 'react';
import kkpService from '../../services/kkpService';
import { confirmVenteClear } from '../../utils/confirmVenteDelete';
import useAppConfirm from '../../hooks/useAppConfirm';
import { filterStatutsForMonth } from '../../utils/kkpStatutUtils';
import { briefCreatifToPayload, venteBriefFromApi } from '../../utils/kkpVenteBriefUtils';
import { briefPiecesJointesFromVente } from '../../utils/kkpBriefFichierUtils';
import { useKkpVenteBriefWizard } from '../../hooks/useKkpVenteBriefWizard';
import {
  filterSemaineOptionsForPeriod,
  generateWebYearWeekOptions,
} from '../../utils/kkpPlanningSemaineOptions';
import KkpPlanningSemaineSelect from './KkpPlanningSemaineSelect';
import KkpClientAutocomplete from './KkpClientAutocomplete';
import { isWebSharedEmplacement } from '../../utils/kkpWebUtils';
import KkpVenteWizardSteps from './KkpVenteWizardSteps';
import KkpVenteBriefCreativeStep from './KkpVenteBriefCreativeStep';
import KkpVenteWizardFooter from './KkpVenteWizardFooter';
import {
  AppFormField,
  AppInput,
  AppModal,
  AppSelect,
} from '../ui';
import {
  batValideOptionTitle,
  interceptGraphisteBatValideRequest,
  interceptLockedBatValideChange,
  isBatValideOptionLocked,
} from '../../utils/kkpBatValide';
import '../../styles/kkp-vente-wizard.scss';

const EMPTY_FORM = {
  annee: 2026,
  mois: new Date().getMonth() + 1,
  semaine: '',
  commercial_id: '',
  client_label: '',
  entreprise_locale_id: '',
  dolibarr_thirdparty_id: '',
  emplacement: 'MEGABANNIERE',
  web_slot_index: 1,
  statut_phase: 'Option',
  affichage: '',
  click_desktop: '',
  click_mobile: '',
  brief_creatif: '',
};

const KkpVenteWebModal = ({
  show,
  onClose,
  onSaved,
  onDeleted,
  onError,
  meta,
  defaultCommercialId = '',
  initialValues = {},
  initialWizardStep,
  vente = null,
  onRequestBatValide,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { ask, modal } = useAppConfirm();
  const {
    currentStep,
    setCurrentStep,
    setSavedVente,
    piecesJointes,
    setPiecesJointes,
    activeVente,
    activeVenteId,
    shouldCloseAfterCreate,
  } = useKkpVenteBriefWizard({ show, vente, initialWizardStep, initialValues });

  const showSlotField = isWebSharedEmplacement(form.emplacement);

  const statutOptions = useMemo(
    () => filterStatutsForMonth(meta?.statuts, form.annee, form.mois),
    [meta?.statuts, form.annee, form.mois],
  );

  const semaineOptions = useMemo(
    () => filterSemaineOptionsForPeriod(
      generateWebYearWeekOptions(Number(form.annee) || new Date().getFullYear()),
      {
        annee: form.annee,
        mois: form.mois,
        includeValue: activeVenteId ? form.semaine : null,
      },
    ),
    [form.annee, form.mois, form.semaine, activeVenteId],
  );

  useEffect(() => {
    if (!show) return;

    if (vente) {
      setForm({
        annee: vente.annee,
        mois: vente.mois,
        semaine: vente.semaine ?? '',
        commercial_id: vente.commercial_id ?? '',
        client_label: vente.client_label,
        entreprise_locale_id: vente.entreprise_locale_id ?? '',
        dolibarr_thirdparty_id: vente.dolibarr_thirdparty_id ?? '',
        emplacement: vente.emplacement ?? vente.format,
        web_slot_index: vente.web_slot_index ?? 1,
        statut_phase: vente.statut_phase,
        affichage: vente.affichage ?? '',
        click_desktop: vente.click_desktop ?? '',
        click_mobile: vente.click_mobile ?? '',
        brief_creatif: venteBriefFromApi(vente.brief_encart),
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        annee: initialValues.annee ?? new Date().getFullYear(),
        mois: initialValues.mois ?? new Date().getMonth() + 1,
        emplacement: initialValues.emplacement ?? meta?.formats?.[0]?.value ?? 'MEGABANNIERE',
        web_slot_index: initialValues.web_slot_index ?? 1,
        statut_phase: initialValues.statut_phase ?? filterStatutsForMonth(meta?.statuts, initialValues.annee ?? 2026, initialValues.mois ?? new Date().getMonth() + 1)[0]?.value ?? 'Option',
        ...initialValues,
        commercial_id: defaultCommercialId || '',
        brief_creatif: venteBriefFromApi(initialValues.brief_encart),
      });
    }
  }, [show, vente, initialValues, defaultCommercialId, meta]);

  useEffect(() => {
    if (!show || statutOptions.length === 0) return;
    if (!statutOptions.some((s) => s.value === form.statut_phase)) {
      setForm((f) => ({ ...f, statut_phase: statutOptions[0].value }));
    }
  }, [show, form.annee, form.mois, form.statut_phase, statutOptions]);

  useEffect(() => {
    if (!show || showSlotField) return;
    if (form.web_slot_index !== 1) {
      setForm((f) => ({ ...f, web_slot_index: 1 }));
    }
  }, [show, showSlotField, form.web_slot_index]);

  useEffect(() => {
    if (!show || !form.semaine) return;
    if (!semaineOptions.some((option) => String(option.value) === String(form.semaine))) {
      setForm((f) => ({ ...f, semaine: '' }));
    }
  }, [show, form.annee, form.mois, form.semaine, semaineOptions]);

  const buildPayload = () => ({
    ...form,
    commercial_id: form.commercial_id || defaultCommercialId || null,
    entreprise_locale_id: form.entreprise_locale_id || null,
    dolibarr_thirdparty_id: form.dolibarr_thirdparty_id || null,
    affichage: form.affichage === '' ? null : Number(form.affichage),
    click_desktop: form.click_desktop === '' ? null : Number(form.click_desktop),
    click_mobile: form.click_mobile === '' ? null : Number(form.click_mobile),
    brief_encart: briefCreatifToPayload(form.brief_creatif),
  });

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = buildPayload();
      let result;
      if (activeVenteId) {
        result = await kkpService.updateVenteWeb(activeVenteId, payload);
      } else {
        result = await kkpService.createVenteWeb(payload);
      }

      onSaved?.(result);
      if (result?.rejected) {
        return;
      }

      const saved = result?.vente ?? null;
      if (saved) {
        setSavedVente(saved);
        setPiecesJointes(briefPiecesJointesFromVente(saved));
      }

      if (activeVenteId || shouldCloseAfterCreate(currentStep)) {
        onClose();
      }
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!activeVenteId) return;
    if (!(await confirmVenteClear(ask, form.client_label))) return;

    setDeleting(true);
    try {
      const result = await kkpService.clearVenteWeb(activeVenteId);
      onDeleted?.(result);
      onClose();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <AppModal
      show={show}
      title={activeVenteId ? 'Modifier la vente' : 'Nouvelle vente WEB'}
      onClose={onClose}
      size="lg"
      centered
      wrapBody={false}
      contentAs="form"
      contentProps={{ onSubmit: handleSave }}
    >

            <KkpVenteWizardSteps currentStep={currentStep} onStepChange={setCurrentStep} />

            <div className="kkp-vente-wizard-body">
              {currentStep === 0 && (
                <div className="row g-3">
                  <AppFormField className="col-md-4" label="Année" htmlFor="kkp-web-annee" required>
                    <AppInput
                      id="kkp-web-annee"
                      type="number"
                      required
                      value={form.annee}
                      onChange={(e) => setForm((f) => ({ ...f, annee: Number(e.target.value), semaine: '' }))}
                    />
                  </AppFormField>
                  <AppFormField className="col-md-4" label="Mois" htmlFor="kkp-web-mois" required>
                    <AppSelect
                      id="kkp-web-mois"
                      required
                      value={form.mois}
                      onChange={(e) => setForm((f) => ({ ...f, mois: Number(e.target.value), semaine: '' }))}
                    >
                      {(meta?.mois ?? []).map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-md-4" label="Semaine" required>
                    <KkpPlanningSemaineSelect
                      value={form.semaine}
                      onChange={(e) => setForm((f) => ({ ...f, semaine: e.target.value }))}
                      options={semaineOptions}
                      allowEmpty
                      emptyLabel={semaineOptions.length ? '— Choisir —' : '— Aucune semaine dispo —'}
                      required
                    />
                  </AppFormField>
                  <AppFormField className="col-12" label="Client">
                    <KkpClientAutocomplete
                      value={form.client_label}
                      onChange={(val) => setForm((f) => ({
                        ...f,
                        client_label: val,
                        entreprise_locale_id: '',
                        dolibarr_thirdparty_id: '',
                      }))}
                      onSelect={(client) => setForm((f) => ({
                        ...f,
                        client_label: client.nom ?? '',
                        dolibarr_thirdparty_id: Number(client?.id) > 0 ? Number(client.id) : '',
                        entreprise_locale_id: '',
                      }))}
                    />
                  </AppFormField>
                  <AppFormField
                    className={showSlotField ? 'col-md-6' : 'col-md-12'}
                    label="Emplacement"
                    htmlFor="kkp-web-emplacement"
                    required
                  >
                    <AppSelect
                      id="kkp-web-emplacement"
                      required
                      value={form.emplacement}
                      onChange={(e) => {
                        const emplacement = e.target.value;
                        setForm((f) => ({
                          ...f,
                          emplacement,
                          web_slot_index: isWebSharedEmplacement(emplacement) ? f.web_slot_index : 1,
                        }));
                      }}
                    >
                      {(meta?.formats ?? []).map((f) => (
                        <option key={f.value} value={f.value}>{f.value}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  {showSlotField && (
                    <AppFormField className="col-md-6" label="Slot (partage)" htmlFor="kkp-web-slot">
                      <AppSelect
                        id="kkp-web-slot"
                        value={form.web_slot_index}
                        onChange={(e) => setForm((f) => ({ ...f, web_slot_index: Number(e.target.value) }))}
                      >
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                      </AppSelect>
                    </AppFormField>
                  )}
                  <AppFormField className="col-md-3" label="Affichages" htmlFor="kkp-web-affichage">
                    <AppInput
                      id="kkp-web-affichage"
                      type="number"
                      min="0"
                      value={form.affichage}
                      onChange={(e) => setForm((f) => ({ ...f, affichage: e.target.value }))}
                    />
                  </AppFormField>
                  <AppFormField className="col-md-3" label="Clics desktop" htmlFor="kkp-web-click-desktop">
                    <AppInput
                      id="kkp-web-click-desktop"
                      type="number"
                      min="0"
                      value={form.click_desktop}
                      onChange={(e) => setForm((f) => ({ ...f, click_desktop: e.target.value }))}
                    />
                  </AppFormField>
                  <AppFormField className="col-md-3" label="Clics mobile" htmlFor="kkp-web-click-mobile">
                    <AppInput
                      id="kkp-web-click-mobile"
                      type="number"
                      min="0"
                      value={form.click_mobile}
                      onChange={(e) => setForm((f) => ({ ...f, click_mobile: e.target.value }))}
                    />
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Statut phase" htmlFor="kkp-web-statut" required>
                    <AppSelect
                      id="kkp-web-statut"
                      required
                      value={form.statut_phase}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (interceptLockedBatValideChange(next, form.statut_phase)) return;
                        if (interceptGraphisteBatValideRequest(next, form.statut_phase, activeVente, onRequestBatValide)) {
                          return;
                        }
                        setForm((f) => ({ ...f, statut_phase: next }));
                      }}
                    >
                      {statutOptions.map((s) => (
                        <option
                          key={s.value}
                          value={s.value}
                          title={batValideOptionTitle(s.value)}
                          style={isBatValideOptionLocked(s.value) ? { color: '#9ca3af' } : undefined}
                        >
                          {s.value}
                        </option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                </div>
              )}

              {currentStep === 1 && (
                <KkpVenteBriefCreativeStep
                  value={form.brief_creatif}
                  onChange={(value) => setForm((f) => ({ ...f, brief_creatif: value }))}
                  venteId={activeVenteId}
                  piecesJointes={piecesJointes}
                  onPiecesJointesChange={setPiecesJointes}
                  onError={onError}
                />
              )}
            </div>

            <KkpVenteWizardFooter
              currentStep={currentStep}
              onStepChange={setCurrentStep}
              onClose={onClose}
              onSave={handleSave}
              onDelete={handleClear}
              saving={saving}
              deleting={deleting}
              showDelete={Boolean(activeVenteId)}
              deleteLabel="Effacer"
              deletingLabel="Effacement…"
              deleteVariant="outline-warning"
              showCompleteLater={currentStep === 1}
            />
    </AppModal>
    {modal}
    </>
  );
};

export default KkpVenteWebModal;
